/**
 * Stepped, seamlessly looping product carousel.
 *
 * The theme's slideshow-component advances by however many slides are currently
 * visible (assets/slideshow.js: nextIndex = current + visibleSlides.length), and
 * its smooth wrap-around only runs when exactly one slide is visible, so a
 * multi-column carousel rewinds across the whole track when it reaches the end.
 *
 * This element advances one slide at a time and never rewinds. After each step
 * settles, the slide that just left the viewport is moved to the end of the track
 * and the scroll offset is reduced by the same amount, so the visual result is
 * identical while fresh slides keep appearing ahead. The scroll offset therefore
 * always returns to 0 and the track never runs out of runway.
 *
 * Autoplay pauses on hover, on keyboard focus, while the tab is hidden and while
 * the section is off screen. It is disabled entirely under prefers-reduced-motion,
 * which is what keeps the automatic motion acceptable without a pause button.
 */

const SETTLE_DELAY = 120;
const EPSILON = 2;

const reducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

class CustomProductCarousel extends HTMLElement {
  #track = null;
  #dots = [];
  #timer = null;
  #settleTimer = null;
  #resizeTimer = null;
  #observer = null;
  #resizeObserver = null;
  #hovered = false;
  #focused = false;
  #onScreen = true;

  connectedCallback() {
    this.#track = this.querySelector('[data-carousel-track]');
    if (!this.#track) return;

    this.#dots = Array.from(this.querySelectorAll('[data-carousel-dot]'));

    this.addEventListener('click', this.#handleClick);
    this.addEventListener('pointerenter', this.#handlePointerEnter);
    this.addEventListener('pointerleave', this.#handlePointerLeave);
    this.addEventListener('focusin', this.#handleFocusIn);
    this.addEventListener('focusout', this.#handleFocusOut);
    this.#track.addEventListener('scroll', this.#handleScroll, { passive: true });
    document.addEventListener('visibilitychange', this.#handleVisibilityChange);

    if ('IntersectionObserver' in window) {
      this.#observer = new IntersectionObserver(this.#handleIntersection);
      this.#observer.observe(this);
    }

    if ('ResizeObserver' in window) {
      this.#resizeObserver = new ResizeObserver(this.#handleResize);
      this.#resizeObserver.observe(this.#track);
    }

    this.#syncDots();
    this.#start();
  }

  disconnectedCallback() {
    this.#stop();
    clearTimeout(this.#settleTimer);
    clearTimeout(this.#resizeTimer);

    this.removeEventListener('click', this.#handleClick);
    this.removeEventListener('pointerenter', this.#handlePointerEnter);
    this.removeEventListener('pointerleave', this.#handlePointerLeave);
    this.removeEventListener('focusin', this.#handleFocusIn);
    this.removeEventListener('focusout', this.#handleFocusOut);
    this.#track?.removeEventListener('scroll', this.#handleScroll);
    document.removeEventListener('visibilitychange', this.#handleVisibilityChange);

    this.#observer?.disconnect();
    this.#resizeObserver?.disconnect();
  }

  /* Public controls ------------------------------------------------------- */

  next() {
    const step = this.#step;
    if (step <= 0 || !this.#scrollable) return;

    this.#track.scrollBy({ left: step, behavior: this.#behavior });
  }

  previous() {
    const step = this.#step;
    if (step <= 0 || !this.#scrollable) return;

    const last = this.#track.lastElementChild;
    if (!last) return;

    // Bring the previous slide in from the end, then compensate the scroll
    // offset explicitly so the visual position is unchanged before animating.
    this.#withoutSnapping(() => {
      const offset = this.#track.scrollLeft;
      this.#track.prepend(last);
      this.#track.scrollLeft = offset + step;
    });

    this.#track.scrollBy({ left: -step, behavior: this.#behavior });
  }

  goTo(index) {
    this.#withoutSnapping(() => {
      let remaining = this.#slides.length + 1;

      while (remaining--) {
        const first = this.#track.firstElementChild;
        if (!first || Number(first.dataset.carouselIndex) === index) break;
        this.#track.append(first);
      }

      this.#track.scrollLeft = 0;
    });

    this.#syncDots();
  }

  /* Geometry -------------------------------------------------------------- */

  get #slides() {
    return Array.from(this.#track.children);
  }

  /** Width of one slide plus the gap, measured from the live layout. */
  get #step() {
    const [first, second] = this.#slides;
    if (!first) return 0;
    if (second) return second.offsetLeft - first.offsetLeft;

    return first.getBoundingClientRect().width;
  }

  get #scrollable() {
    return this.#track.scrollWidth - this.#track.clientWidth > EPSILON;
  }

  get #behavior() {
    return reducedMotion() ? 'auto' : 'smooth';
  }

  /* Rotation -------------------------------------------------------------- */

  /**
   * Moves every slide that has scrolled fully out of view to the end of the
   * track, reducing the scroll offset by the same amount each time. The slide is
   * off screen at that point, so nothing visible changes.
   */
  #normalize = () => {
    const step = this.#step;
    if (step <= 0) return;

    this.#withoutSnapping(() => {
      let remaining = this.#slides.length + 1;

      while (this.#track.scrollLeft >= step - EPSILON && remaining--) {
        const first = this.#track.firstElementChild;
        if (!first) break;

        const offset = this.#track.scrollLeft;
        this.#track.append(first);
        this.#track.scrollLeft = offset - step;
      }
    });

    this.#syncDots();
  };

  /**
   * Reordering children of a container with mandatory scroll snapping can make
   * the browser re-snap to a different slide, which would show as a jump. Snap
   * is suspended for the mutation and restored on the next frame.
   */
  #withoutSnapping = (mutate) => {
    const previous = this.#track.style.scrollSnapType;
    this.#track.style.scrollSnapType = 'none';

    mutate();

    requestAnimationFrame(() => {
      this.#track.style.scrollSnapType = previous;
    });
  };

  #syncDots = () => {
    if (!this.#dots.length) return;

    const index = Number(this.#track.firstElementChild?.dataset.carouselIndex ?? 0);

    for (const dot of this.#dots) {
      const active = Number(dot.dataset.carouselDot) === index;

      if (active) {
        dot.setAttribute('aria-current', 'true');
      } else {
        dot.removeAttribute('aria-current');
      }
    }
  };

  /* Autoplay -------------------------------------------------------------- */

  get #autoplayEnabled() {
    return this.getAttribute('autoplay') === 'true' && !reducedMotion();
  }

  get #interval() {
    const seconds = parseFloat(this.getAttribute('interval'));

    return (Number.isFinite(seconds) && seconds > 0 ? seconds : 5) * 1000;
  }

  #start = () => {
    this.#stop();
    if (!this.#autoplayEnabled) return;

    this.#timer = setInterval(this.#tick, this.#interval);
  };

  #stop = () => {
    clearInterval(this.#timer);
    this.#timer = null;
  };

  #restart = () => {
    if (this.#timer) this.#start();
  };

  #tick = () => {
    if (this.#hovered || this.#focused || document.hidden || !this.#onScreen) return;

    this.next();
  };

  /* Events ---------------------------------------------------------------- */

  #handleClick = (event) => {
    if (event.target.closest('[data-carousel-previous]')) {
      this.#restart();
      this.previous();
      return;
    }

    if (event.target.closest('[data-carousel-next]')) {
      this.#restart();
      this.next();
      return;
    }

    const dot = event.target.closest('[data-carousel-dot]');
    if (!dot) return;

    this.#restart();
    this.goTo(Number(dot.dataset.carouselDot));
  };

  #handleScroll = () => {
    clearTimeout(this.#settleTimer);
    this.#settleTimer = setTimeout(this.#normalize, SETTLE_DELAY);
  };

  #handleResize = () => {
    clearTimeout(this.#resizeTimer);
    this.#resizeTimer = setTimeout(() => {
      this.#track.scrollLeft = 0;
      this.#syncDots();
    }, SETTLE_DELAY);
  };

  #handlePointerEnter = () => {
    this.#hovered = true;
  };

  #handlePointerLeave = () => {
    this.#hovered = false;
  };

  #handleFocusIn = () => {
    this.#focused = true;
  };

  #handleFocusOut = () => {
    this.#focused = false;
  };

  #handleVisibilityChange = () => {
    if (!document.hidden) this.#restart();
  };

  #handleIntersection = (entries) => {
    for (const entry of entries) {
      this.#onScreen = entry.isIntersecting;
    }
  };
}

if (!customElements.get('custom-product-carousel')) {
  customElements.define('custom-product-carousel', CustomProductCarousel);
}
